import React, { useMemo, useState } from "react";
import { InView } from "react-intersection-observer";
import CountryFlag from "react-country-flag";
import { motion, AnimatePresence } from "framer-motion";
import { useWindowSize } from "../../hooks/useWindowSize";
import styles from "../OOMLeaderboard/OOMLeaderboard.module.css";
import GridWrapper from "../GridWrapper/GridWrapper";
import GridElement from "../GridElement/GridElement";
import { groupInnerVariants } from "../Leaderboard/animations";
import { useDebounce } from "../../hooks/useDebounce";
import SearchIcon from "@mui/icons-material/Search";

interface Earnings {
  csvFile: Array<any>;
}

const Earnings = ({ csvFile }: Earnings) => {
  const { windowSize } = useWindowSize();
  const [activeRows, setActiveRows] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  const handleRowClick = (id: string) => {
    setActiveRows((prevRows) =>
      prevRows.includes(id)
        ? prevRows.filter((i) => i !== id)
        : [...prevRows, id]
    );
  };

  const debouncedSearchText = useDebounce(searchText, 300);

  const filteredData = useMemo(() => {
    if (!debouncedSearchText) return csvFile?.slice(1) || []; // Return all rows if no search text
    return (
      csvFile
        ?.slice(1) // Exclude the header row
        .filter((row: any) =>
          row.some((cell: any) =>
            cell
              ?.toString()
              .toLowerCase()
              .includes(debouncedSearchText.toLowerCase())
          )
        ) || []
    );
  }, [debouncedSearchText]);

  const handleSearchInputChange = (event: { target: { value: any } }) => {
    const searchText = event.target.value;
    setSearchText(searchText);
  };

  return (
    <GridWrapper className={styles.root}>
      <GridElement
        gridLayout={[
          [961, 2, 14],
          [1401, 4, 26],
        ]}
      >
        <GridWrapper>
          <GridElement
            gridLayout={[
              [961, 1, 16, 2],
              [1401, 1, 32],
            ]}
          >
            <div className="flex flex-col md:flex-row justify-center lg:justify-end items-center gap-[10px] mx-0 mb-[10px] md:mt-14">
              <div className="flex items-center w-full max-w-[330px] lg:max-w-[280px] min-h-[45px] text-2xl bg-[#343434] rounded-[10px] cursor-pointer px-4">
                <input
                  type="search"
                  placeholder="Search for player"
                  value={searchText}
                  onChange={handleSearchInputChange}
                  className="custom-search-input w-full border-none bg-transparent p-0 m-0 text-2xl focus-visible:outline-none"
                />
                <SearchIcon
                  fontSize="large"
                  className="text-white cursor-pointer"
                />
              </div>
            </div>
          </GridElement>
        </GridWrapper>
      </GridElement>
      <GridElement
        gridLayout={[
          [961, 2, 14, 2],
          [1401, 4, 26, 2],
        ]}
      >
        <div className="pb-32 overflow-y-auto scroll-smooth">
          {csvFile && csvFile?.length > 0 && (
            <div role="table" className={styles.leaderboardTable}>
              <div role="thead" className={styles.tableHeader}>
                {csvFile[0].slice(0, 4).map((i: string, n: number) => (
                  <div
                    role="th"
                    className={styles.tableHeaderCell}
                    key={`${i}-${n}`}
                  >
                    {n === 1 ? (
                      <span className={styles.thCellLabel}>{i}</span>
                    ) : (
                      i
                    )}
                  </div>
                ))}
              </div>
              <div role="tbody" className={styles.tableBody}>
                {filteredData.map((i: any, index: number) => {
                  const rowID: string =
                    i[0] + i[2]?.replace(/\s/g, "") + i[3] || i[0] + index;

                  const filteredDataFromCSV = csvFile[0].filter(
                    (str: string) => !!str?.length
                  );

                  return (
                    <InView
                      key={`${rowID}-${index}`}
                      threshold={0.5}
                      triggerOnce
                    >
                      {({ inView, ref }) => (
                        <div
                          ref={ref}
                          role="tr"
                          className={`${styles.tableRowGroup}${
                            activeRows.includes(rowID)
                              ? " " + styles.tableRowGroupActive
                              : ""
                          }`}
                        >
                          {inView ? (
                            <div>
                              <div
                                role="tr"
                                className={styles.tableRow}
                                onClick={() => handleRowClick(rowID)}
                              >
                                {i.slice(0, 4).map((d: any, n: number) => (
                                  <div
                                    role="td"
                                    className={styles.tableRowCell}
                                    key={`${index}-${n}-${d}`}
                                  >
                                    {n === 1 ? (
                                      d ? (
                                        <CountryFlag
                                          countryCode={d}
                                          style={{ fontSize: "1.5em" }}
                                          svg
                                        />
                                      ) : (
                                        ""
                                      )
                                    ) : (
                                      <span className={styles.data}>{d}</span>
                                    )}
                                  </div>
                                ))}
                              </div>

                              <AnimatePresence>
                                {activeRows.includes(rowID) && (
                                  <motion.div
                                    role="rowgroup"
                                    className={`${styles.tableRowGroupInner}${
                                      filteredDataFromCSV.length > 10 &&
                                      windowSize.width < 961
                                        ? " " + styles.tableRowGroupInnerLarge
                                        : ""
                                    }`}
                                    variants={groupInnerVariants}
                                    initial="initial"
                                    animate={
                                      activeRows.includes(rowID)
                                        ? "open"
                                        : "initial"
                                    }
                                    exit="initial"
                                  >
                                    <div
                                      className={
                                        styles.tableRowGroupInnerContent
                                      }
                                    >
                                      {filteredDataFromCSV.length <= 10 ? (
                                        <>
                                          <div
                                            role="thead"
                                            className={styles.tableHeader}
                                          >
                                            {filteredDataFromCSV
                                              .slice(
                                                4,
                                                filteredDataFromCSV.length
                                              )
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`table-header-${d}-${n}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className={styles.tableRow}
                                          >
                                            {i
                                              .slice(
                                                4,
                                                filteredDataFromCSV.length
                                              )
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`tr-${n}-${d}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                        </>
                                      ) : windowSize.width > 960 ? (
                                        <>
                                          <div
                                            role="thead"
                                            className={styles.tableHeader}
                                          >
                                            {filteredDataFromCSV
                                              .slice(4, csvFile.length)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`table-header-${n}-${d}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className={styles.tableRow}
                                          >
                                            {i
                                              .slice(
                                                4,
                                                filteredDataFromCSV.length
                                              )
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`table-row-${n}-${d}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div
                                            role="thead"
                                            className={styles.tableHeader}
                                          >
                                            {filteredDataFromCSV
                                              .slice(4, 7)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`thead-td-${n}-${d}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className={styles.tableRow}
                                          >
                                            {i
                                              .slice(4, 7)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`tr-td-${n}-${d}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="thead"
                                            className={styles.tableHeader}
                                          >
                                            {filteredDataFromCSV
                                              .slice(7, 11)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`thead-row-cell-${n}-${d}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                          <div
                                            role="tr"
                                            className={styles.tableRow}
                                          >
                                            {i
                                              .slice(7, 11)
                                              .map((d: string, n: number) => (
                                                <div
                                                  role="td"
                                                  className={
                                                    styles.tableRowCell
                                                  }
                                                  key={`tr-cell-${n}-${d}`}
                                                >
                                                  <span className={styles.data}>
                                                    {d}
                                                  </span>
                                                </div>
                                              ))}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ) : (
                            <div className={styles.tableRow}></div>
                          )}
                        </div>
                      )}
                    </InView>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </GridElement>
    </GridWrapper>
  );
};

export default Earnings;
